import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const LanguageSwitcher = ({ variant = "light" }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentCode = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === currentCode) ||
    SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const isDark = variant === "dark"; // when placed on a dark background
  const btnClasses = isDark
    ? "text-white/80 hover:text-white border-white/20 hover:border-white/40"
    : "text-[#4A5543] hover:text-[#C5A059] border-[#4A5543]/20 hover:border-[#C5A059]/40";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs tracking-[0.15em] uppercase font-medium transition-colors ${btnClasses}`}
        data-testid="lang-switcher-btn"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Globe className="w-3.5 h-3.5" strokeWidth={1.75} />
        <span>{current.short}</span>
        <ChevronDown
          className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <ul
          role="menu"
          className="absolute right-0 mt-2 min-w-[10rem] rounded-2xl bg-white shadow-[0_20px_60px_rgba(44,53,36,0.18)] border border-[#E3DAC9] py-2 z-50"
          data-testid="lang-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <li key={lang.code} role="menuitem">
                <button
                  type="button"
                  onClick={() => handleSelect(lang.code)}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
                    active
                      ? "text-[#C5A059] bg-[#F9F8F6]"
                      : "text-[#2C3524] hover:bg-[#F9F8F6]"
                  }`}
                  data-testid={`lang-option-${lang.code}`}
                >
                  <span>{lang.label}</span>
                  {active && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
